import React, { useState, useEffect } from "react";
import { Box, Typography, Button, TextField } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import loginImage from "../../assets/images/loginImage.svg";
import loginLogo from "../../assets/images/LoginLogo.svg";
import { VerifyCredentials, getOTP } from "../../redux/actions/auth/auth";
import { clearError } from "redux/features/login/loginSlice";
import {
  mxSigninEnterEmail,
  mxSigninFail,
  mxSigninSuccess,
} from "../../utils/mixpanelEvents";
import OTPInput from "./OtpInput";
import { maskString } from "utils/functions/maskemail";

const Login = () => {
  const [email, setEmail] = useState("");
  const [showOTPStep, setShowOTPStep] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [localError, setLocalError] = useState(null);
  const [seconds, setSeconds] = useState(45);
  const [otpValue, setOtpValue] = useState(""); // Add state to track OTP value

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get state from Redux store
  const {
    loading,
    error: reduxError,
    otpSent,
  } = useSelector((state) => state.login);

  // Clear errors when component mounts or email changes
  useEffect(() => {
    dispatch(clearError());
    setLocalError(null);
  }, [dispatch, email]);

  // Step 1: Email validation and OTP request
  const submitEmailForm = async () => {
    const validEmailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

    // Client-side validation
    if (!email || !email.length > 0 || !email.match(validEmailRegex)) {
      setLocalError("Invalid email provided.");
      return;
    }

    try {
      setLocalError(null);
      dispatch(clearError());

      await dispatch(getOTP({ email })).unwrap();
      setShowOTPStep(true);
      setSeconds(45); // Reset timer when OTP is sent
      mxSigninEnterEmail();
    } catch (err) {
      console.log("API Error:", err);
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    try {
      setOtpError("");
      dispatch(clearError());

      await dispatch(getOTP({ email })).unwrap();
      setSeconds(45); // Reset timer
      setOtpValue(""); // Clear current OTP value
    } catch (err) {
      setOtpError("Failed to resend OTP. Please try again.");
      console.log("Resend OTP Error:", err);
    }
  };

  // Step 2: OTP verification
  const handleOtpComplete = async (otpVal) => {
    setOtpError("");

    if (!otpVal || otpVal.length !== 6) {
      setOtpError("Please enter a complete 6-digit OTP.");
      return;
    }

    try {
      await dispatch(
        VerifyCredentials({
          email: email,
          otp: otpVal,
        })
      ).unwrap();

      if (localStorage.getItem("token")) {
        mxSigninSuccess();
        window.location.href = "/";
      }
    } catch (err) {
      setOtpError(err.error || err.message || "Invalid OTP. Please try again.");
      mxSigninFail();
    }
  };

  // Timer effect
  useEffect(() => {
    let timer;
    if (seconds > 0 && showOTPStep) {
      timer = window.setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1);
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [seconds, showOTPStep]);

  const handleBackToEmail = () => {
    setShowOTPStep(false);
    setOtpError("");
    setLocalError(null);
    setOtpValue("");
    setSeconds(45);
    dispatch(clearError());
  };

  // Handle OTP change from child component
  const handleOtpChange = (newOtpValue) => {
    setOtpValue(newOtpValue);
    setOtpError(""); // Clear error when user starts typing
  };

  // Determine which error to show
  const displayError = localError || reduxError;

  return (
    <div className="main_login">
      <div className="animate__animated animate__fadeIn container-fluid">
        <div className="row login_details_wrp_row">
          <Col lg={6} sm={12} className="login_details_wrp_col1 d-flex">
            <div className="loginContent col-lg-12">
              <img className="loginLogo" src={loginLogo} />
              <span className="loginHeading">
                We Make HCC Coding Education Easy.
              </span>
              <p className="loginText">
                DoctusTech App is the best way for clinicians to learn HCC{" "}
                <br />
                Coding. 9 doctors prefer DoctusTech to any other <br />
                method of HCC Training.
              </p>
              <img className="loginImage" src={loginImage} />
            </div>
          </Col>
          <Col
            lg={6}
            sm={12}
            className="p-0 d-flex flex-column justify-content-center login_details_wrp">
            <div className="login_details">
              {!showOTPStep ? (
                // Step 1: Email Input
                <>
                  <Box
                    component="form"
                    className="dt-login-right-section-1"
                    sx={{
                      padding: 4,
                      borderRadius: 2,
                      boxShadow: 1,
                      backgroundColor: "#FFFFFF",
                      maxWidth: "400px",
                    }}>
                    <Typography
                      variant="h5"
                      component="h2"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}>
                      Welcome To Doctustech
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      Please sign-in to your account
                    </Typography>
                    <TextField
                      label="Enter your email"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      onChange={(e) => setEmail(e.target.value)}
                      value={email || ""}
                      placeholder={"Enter your email"}
                      sx={{ my: 2 }}
                      InputLabelProps={{
                        style: {
                          color: displayError ? "#F44336" : "#333",
                        },
                      }}
                      error={Boolean(displayError)}
                      helperText={displayError || ""}
                    />
                    <Button
                      type="submit"
                      color="secondary"
                      variant="contained"
                      onClick={submitEmailForm}
                      fullWidth
                      disabled={loading || !email}
                      sx={{
                        minHeight: "40px",
                        backgroundColor: "rgb(13, 66, 106)",
                        "&:hover": {
                          backgroundColor: "rgb(13, 66, 106)",
                        },
                      }}>
                      NEXT
                    </Button>
                    <Typography
                      mt={2}
                      sx={{ color: "black !important" }}
                      display="block"
                      gutterBottom
                      textAlign={"start"}>
                      <span style={{ color: "#808080" }}>
                        I agree that I have read and accepted the{" "}
                        <a href="https://policies.google.com/terms">
                          Terms of Service
                        </a>{" "}
                        and{" "}
                        <a href="https://policies.google.com/privacy">
                          Privacy Policy
                        </a>{" "}
                        apply.
                      </span>
                    </Typography>
                    <p
                      className="login_detail_inftext"
                      style={{ textAlign: "start" }}>
                      This site is protected by reCAPTCHA and the Google
                      <a
                        href="https://www.doctustech.com/privacy-policy/"
                        rel="noreferrer"
                        target="_blank">
                        Privacy Policy
                      </a>
                      and
                      <a
                        href="https://www.doctustech.com/term-of-use/"
                        rel="noreferrer"
                        target="_blank">
                        Terms of Service.
                      </a>
                    </p>
                  </Box>
                </>
              ) : (
                // Step 2: OTP Input
                <>
                  <Button
                    onClick={handleBackToEmail}
                    id="dt-login-otp-backbtn"
                    sx={{ color: "grey" }}>
                    <ArrowBackIcon sx={{ height: "20px", width: "20px" }} />
                    Back
                  </Button>
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      width: "100%",
                      maxWidth: "400px",
                      alignItems: "center",
                      textAlign: "center",
                    }}>
                    <Box
                      sx={{
                        padding: 4,
                        borderRadius: 2,
                        boxShadow: 1,
                        alignItems: "center",
                        textAlign: "center",
                      }}>
                      <h4 style={{ fontWeight: "bold" }}>Verify Code</h4>
                      <p
                        style={{
                          fontSize: "14px",
                          marginBottom: "20px",
                        }}>{`The code has been sent to the email you provided`}</p>

                      <p
                        style={{
                          fontSize: "16px",
                          marginBottom: "20px",
                          fontWeight: "bold",
                        }}>
                        {maskString(email, 6, 6)}
                      </p>

                      <OTPInput
                        error={otpError}
                        onOtpChange={handleOtpChange}
                        resetOtp={seconds === 45} // Reset when new OTP is sent
                      />
                      {reduxError && (
                        <Typography
                          variant="caption"
                          display="block"
                          gutterBottom
                          color="red"
                          align="center"
                          width="100%">
                          {reduxError}
                        </Typography>
                      )}

                      <Button
                        type="submit"
                        color="secondary"
                        variant="contained"
                        onClick={() => handleOtpComplete(otpValue)}
                        fullWidth
                        disabled={loading || otpValue.length !== 6}
                        sx={{
                          minHeight: "40px",
                          backgroundColor: "rgb(13, 66, 106)",
                          "&:hover": {
                            backgroundColor: "rgb(13, 66, 106)",
                          },
                        }}>
                        Sign in
                      </Button>

                      <p
                        style={{
                          marginTop: "10px",
                        }}>{`Didn't receive the verification code?`}</p>

                      {seconds !== 0 ? (
                        <p>
                          Resend Code in: <b>{seconds}</b>s
                        </p>
                      ) : (
                        <div
                          onClick={handleResendOtp}
                          style={{
                            cursor: "pointer",
                            color: "#2ABDF0",
                            textDecoration: "underline",
                            marginTop: "10px",
                          }}>
                          Resend OTP
                        </div>
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </div>
          </Col>
        </div>
      </div>
    </div>
  );
};

export default Login;
